import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Loader2, CheckCircle2, Play, X } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Register({ onWatch }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Lütfen tüm alanları doldurun.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/register`, form);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kayıt alınamadı. Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setDone(false);
    setForm({ name: "", email: "", phone: "" });
  };

  return (
    <div
      id="kayit"
      className="scroll-mt-24 w-full rounded-2xl bg-white shadow-[0_24px_60px_rgba(18,58,114,0.22)] ring-1 ring-slate-100 overflow-hidden"
    >
      <AnimatePresence mode="wait">
        {!done ? (
          <motion.div
            key="form"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35 }}
            className="p-6 sm:p-8"
          >
            <div className="w-12 h-1.5 bg-gold rounded-full mb-4" />
            <h2 className="font-extrabold text-navy tracking-tight text-xl sm:text-2xl leading-snug">
              HEMEN BİLGİ ALMAK ve KAZANMAYA BAŞLAMAK İÇİN KAYIT OL!
            </h2>

            <form data-testid="register-form" onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-navy mb-1.5">İSİM SOYİSİM</label>
                <Input
                  data-testid="register-name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="İsim Soyisim"
                  className="h-12 bg-slate-50 border-slate-200 focus-visible:ring-brand"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-navy mb-1.5">E-POSTA ADRESİ</label>
                <Input
                  data-testid="register-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="ornek@eposta.com"
                  className="h-12 bg-slate-50 border-slate-200 focus-visible:ring-brand"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-navy mb-1.5">TELEFON NUMARASI</label>
                <Input
                  data-testid="register-phone"
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+90 5xx xxx xx xx"
                  className="h-12 bg-slate-50 border-slate-200 focus-visible:ring-brand"
                />
              </div>

              <button
                data-testid="register-submit"
                type="submit"
                disabled={loading}
                className="group inline-flex items-center justify-center gap-2 w-full rounded-md bg-brand px-7 py-3.5 text-white font-bold tracking-wide transition-all duration-300 hover:bg-brand-dark hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> GÖNDERİLİYOR...
                  </>
                ) : (
                  <>
                    KAYIT OL
                    <Send className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>

              <p className="text-slate-500 text-sm text-center pt-1 leading-relaxed">
                Danışmanlarımız kısa sürede sizlerle iletişime geçecektir. İyi günler dileriz.
              </p>
            </form>
          </motion.div>
        ) : (
          <motion.div
            key="success"
            data-testid="register-success"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.35 }}
            className="p-8 sm:p-10 text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, type: "spring", stiffness: 200, damping: 14 }}
              className="mx-auto grid place-items-center w-20 h-20 rounded-full bg-green-100 text-green-600 mb-6"
            >
              <CheckCircle2 className="w-11 h-11" strokeWidth={2} />
            </motion.div>
            <h2 className="font-extrabold text-navy tracking-tight text-2xl">
              Tebrikler! Başarı ile kayıt oldunuz.
            </h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Danışmanlarımız detaylı bilgilendirme için sizlerle kısa sürede iletişime geçecektir.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                data-testid="register-success-ok"
                onClick={reset}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-7 py-3.5 text-navy font-bold tracking-wide transition-all duration-300 hover:bg-slate-50 hover:-translate-y-0.5 w-full sm:w-auto"
              >
                <X className="w-4 h-4" /> TAMAM
              </button>
              <button
                data-testid="register-success-video"
                onClick={() => onWatch?.()}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-brand px-7 py-3.5 text-white font-bold tracking-wide transition-all duration-300 hover:bg-brand-dark hover:-translate-y-0.5 w-full sm:w-auto"
              >
                <Play className="w-4 h-4 fill-white" /> EĞİTİM VİDEOSU
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
